import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { authApi } from "@/lib/api";
import { getCurrency, CURRENCIES } from "@/lib/currencies";
import { t as translate } from "@/lib/i18n";
import axios from "axios";

const AuthContext = createContext(null);

// System is locked to English. Any UI translation lookup falls back to en.
const LANGUAGE = "en";

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("sv_token"));
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem("sv_user");
    return u ? JSON.parse(u) : null;
  });
  const [trial, setTrial] = useState(() => {
    const t = localStorage.getItem("sv_trial");
    return t ? JSON.parse(t) : null;
  });
  // ``accessStatus`` is the single field /api/auth/me exposes for
  // routing decisions: "active" / "trialing" / "trial_expired" /
  // "past_due" / "cancelled" / "blocked". The ProtectedRoute reads it.
  const [accessStatus, setAccessStatus] = useState(() => {
    const u = localStorage.getItem("sv_user");
    if (!u) return null;
    try {
      return JSON.parse(u).access_status || null;
    } catch (_e) {
      return null;
    }
  });
  const [currency, setCurrency] = useState("GBP");

  // i18n is now a no-op wrapper around the English dictionary so existing
  // call sites (i18n("vault.TAX_CUSTODY") etc.) keep working without churn.
  const i18n = useCallback((key) => translate(key, LANGUAGE), []);

  const login = useCallback(async (email, password) => {
    const res = await authApi.login(email, password);
    const { token: t, user: u, trial: tr } = res.data;
    localStorage.setItem("sv_token", t);
    localStorage.setItem("sv_user", JSON.stringify(u));
    if (tr) localStorage.setItem("sv_trial", JSON.stringify(tr));
    setToken(t);
    setUser(u);
    setTrial(tr || null);
    setAccessStatus(u?.access_status || null);
    return { user: u, trial: tr };
  }, []);

  const register = useCallback(async (data) => {
    const res = await authApi.register(data);
    const { token: t, user: u, trial: tr } = res.data;
    localStorage.setItem("sv_token", t);
    localStorage.setItem("sv_user", JSON.stringify(u));
    if (tr) localStorage.setItem("sv_trial", JSON.stringify(tr));
    setToken(t);
    setUser(u);
    setTrial(tr || null);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("sv_token");
    localStorage.removeItem("sv_user");
    localStorage.removeItem("sv_trial");
    setToken(null);
    setUser(null);
    setTrial(null);
    setAccessStatus(null);
  }, []);

  // Provider-agnostic session setter used by the Google sign-in
  // callback. ``data`` matches the shape /api/auth/login and
  // /api/auth/google/callback both return.
  const setSession = useCallback((data) => {
    const { token: t, user: u, trial: tr } = data || {};
    if (t) localStorage.setItem("sv_token", t);
    if (u) localStorage.setItem("sv_user", JSON.stringify(u));
    if (tr) localStorage.setItem("sv_trial", JSON.stringify(tr));
    setToken(t || null);
    setUser(u || null);
    setTrial(tr || null);
    setAccessStatus(u?.access_status || null);
  }, []);

  // Refresh access status from /api/auth/me whenever the token
  // changes. Keeps the gate honest if a trial expires mid-session
  // (the JWT itself doesn't carry access_status).
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    authApi.me()
      .then((res) => {
        if (cancelled) return;
        const fresh = res?.data || {};
        setUser((prev) => {
          const next = { ...(prev || {}), ...fresh };
          try { localStorage.setItem("sv_user", JSON.stringify(next)); } catch (_e) {}
          return next;
        });
        setAccessStatus(fresh.access_status || null);
      })
      .catch(() => {
        // /me failure is non-fatal — protected routes will 401 if the
        // token is gone, and ProtectedRoute will redirect to /.
      });
    return () => { cancelled = true; };
  }, [token]);

  // Global 402 interceptor — any product API call that returns
  // ``trial_expired`` must redirect the SPA to /trial-expired.
  // Installed once at provider mount; safe to install in StrictMode
  // because we capture and remove the handler on cleanup.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const reason = (detail && typeof detail === "object" && detail.reason)
          || err?.response?.data?.reason;
        if (status === 402 && reason === "trial_expired") {
          setAccessStatus("trial_expired");
          if (typeof window !== "undefined"
              && !window.location.pathname.startsWith("/trial-expired")) {
            window.location.assign("/trial-expired");
          }
        }
        return Promise.reject(err);
      },
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const RATES = { GBP: 1, AED: 4.6043, USD: 1.2533 };
  const SYMBOLS = { GBP: "\u00a3", AED: "AED", USD: "$" };

  const formatMoney = useCallback((amount, opts = {}) => {
    const cur = getCurrency(currency);
    const converted = amount * cur.rate;
    const decimals = opts.decimals ?? (cur.decimals !== undefined ? cur.decimals : 0);
    // Locked to UK English number formatting (1,234.56)
    const formatted = new Intl.NumberFormat("en-GB", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(converted);
    return `${cur.symbol} ${formatted}`;
  }, [currency]);

  return (
    <AuthContext.Provider value={{ token, user, trial, accessStatus, login, register, logout, setSession, currency, setCurrency, formatMoney, language: LANGUAGE, i18n }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
